import { StyleSheet, css } from 'aphrodite'
import { useCallback, useEffect, useRef, useState } from 'react'
import { projectService } from '../../services/projectService'
import type { Project } from '../../types/project'

type SenderType = 'SUB_HAULER' | 'BROKER'

interface ParentProjectSelectorProps {
  onSelect: (project: Project | null) => void
  selectedProject: Project | null
  disabled?: boolean
  error?: string
  showError?: boolean
  /** The type of sender creating the downstream project */
  senderType?: SenderType
}

const PAGE_SIZE = 10

const ParentProjectSelector: React.FC<ParentProjectSelectorProps> = ({
  onSelect,
  selectedProject,
  disabled = false,
  error,
  showError = false,
  senderType = 'SUB_HAULER',
}) => {
  const [projects, setProjects] = useState<Project[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  })

  // Refs
  const sentinelRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputWrapperRef = useRef<HTMLDivElement>(null)

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Fetch projects with pagination
  const fetchProjects = useCallback(
    async (pageNum: number, search: string, append = false) => {
      if (pageNum === 1) {
        setIsLoading(true)
      } else {
        setIsLoadingMore(true)
      }

      try {
        const data =
          senderType === 'BROKER'
            ? await projectService.getAcceptedProjectsAsBroker({
                page: pageNum,
                page_size: PAGE_SIZE,
                search: search || undefined,
              })
            : await projectService.getAcceptedProjectsAsSubHauler({
                page: pageNum,
                page_size: PAGE_SIZE,
                search: search || undefined,
              })

        // Handle both paginated response and legacy array response
        const items = data.items || (Array.isArray(data) ? data : [])
        const count = data.count ?? items.length
        const pages = data.pages ?? 1

        if (append) {
          setProjects((prev) => [...prev, ...items])
        } else {
          setProjects(items)
        }

        setTotalCount(count)
        setHasMore(pageNum < pages)
        setPage(pageNum)
      } catch (err) {
        console.error('Failed to fetch accepted projects:', err)
        if (!append) {
          setProjects([])
        }
        setHasMore(false)
      } finally {
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    [senderType],
  )

  // Initial fetch and search changes reset to page 1
  useEffect(() => {
    if (isOpen) {
      setPage(1)
      setHasMore(true)
      fetchProjects(1, debouncedSearchTerm, false)
    }
  }, [debouncedSearchTerm, isOpen, fetchProjects])

  // Infinite scroll with IntersectionObserver
  useEffect(() => {
    if (!isOpen || !hasMore || isLoading || isLoadingMore) return

    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          fetchProjects(page + 1, debouncedSearchTerm, true)
        }
      },
      {
        root: dropdownRef.current,
        rootMargin: '100px',
        threshold: 0.1,
      },
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [
    isOpen,
    hasMore,
    isLoading,
    isLoadingMore,
    page,
    debouncedSearchTerm,
    fetchProjects,
  ])

  const handleSelect = (project: Project) => {
    onSelect(project)
    setIsOpen(false)
    setSearchTerm('')
  }

  const handleClear = () => {
    onSelect(null)
  }

  // Calculate dropdown position based on input wrapper
  const calculatePosition = useCallback(() => {
    if (inputWrapperRef.current) {
      const rect = inputWrapperRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + 4, // 4px gap below the input
        left: rect.left,
        width: rect.width,
      })
    }
  }, [])

  // Recalculate position on scroll/resize when open
  useEffect(() => {
    if (!isOpen) return

    calculatePosition()

    window.addEventListener('scroll', calculatePosition, true)
    window.addEventListener('resize', calculatePosition)

    return () => {
      window.removeEventListener('scroll', calculatePosition, true)
      window.removeEventListener('resize', calculatePosition)
    }
  }, [isOpen, calculatePosition])

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        inputWrapperRef.current &&
        !inputWrapperRef.current.contains(target)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const handleFocus = () => {
    calculatePosition()
    setIsOpen(true)
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const hasError = showError && error

  // Dynamic text based on sender type
  const recipientLabel = senderType === 'BROKER' ? 'broker' : 'sub-hauler'
  const emptyMessage =
    senderType === 'BROKER'
      ? 'No accepted projects found where you are a broker'
      : 'No accepted projects found where you are a sub-hauler'
  const helpText =
    senderType === 'BROKER'
      ? `Select an accepted project to create sub-contract terms for another ${recipientLabel}. Job details will be copied from the parent project.`
      : `Select an accepted project to create sub-contract terms for another ${recipientLabel}. Job details will be copied from the parent project.`

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <>
      {[1, 2, 3].map((i) => (
        <div key={i} className={css(styles.skeletonItem)}>
          <div className={css(styles.skeletonHeader)}>
            <div className={css(styles.skeletonLabel)} />
            <div className={css(styles.skeletonDate)} />
          </div>
          <div className={css(styles.skeletonDetails)}>
            <div className={css(styles.skeletonDetail)} />
            <div className={css(styles.skeletonDetail)} />
          </div>
        </div>
      ))}
    </>
  )

  return (
    <div className={css(styles.container)}>
      <label className={css(styles.label)}>
        Parent Project <span className={css(styles.required)}>*</span>
      </label>

      {selectedProject ? (
        <div className={css(styles.selectedProject)}>
          <div className={css(styles.selectedProjectInfo)}>
            <div className={css(styles.selectedProjectLabel)}>
              {selectedProject.label}
            </div>
            <div className={css(styles.selectedProjectDetails)}>
              {selectedProject.job_site && (
                <span>{selectedProject.job_site}</span>
              )}
              {selectedProject.material && (
                <>
                  <span className={css(styles.separator)}>•</span>
                  <span>{selectedProject.material}</span>
                </>
              )}
            </div>
          </div>
          {!disabled && (
            <button
              type="button"
              className={css(styles.clearButton)}
              onClick={handleClear}
            >
              <i className="fa-solid fa-times" />
            </button>
          )}
        </div>
      ) : (
        <div className={css(styles.searchContainer)}>
          <div
            ref={inputWrapperRef}
            className={css(
              styles.searchInputWrapper,
              hasError && styles.searchInputWrapperError,
            )}
          >
            <i className={`fa-solid fa-search ${css(styles.searchIcon)}`} />
            <input
              type="text"
              className={css(styles.searchInput)}
              placeholder="Search accepted projects..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
              }}
              onFocus={handleFocus}
              disabled={disabled}
            />
            {(isLoading || isLoadingMore) && (
              <i
                className={`fa-solid fa-spinner fa-spin ${css(styles.loadingIcon)}`}
              />
            )}
          </div>

          {isOpen && !disabled && (
            <div
              ref={dropdownRef}
              className={css(styles.dropdown)}
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
              }}
            >
              {isLoading ? (
                <LoadingSkeleton />
              ) : projects.length === 0 ? (
                <div className={css(styles.emptyMessage)}>
                  {searchTerm
                    ? 'No projects found matching your search'
                    : emptyMessage}
                </div>
              ) : (
                <>
                  {totalCount > 0 && (
                    <div className={css(styles.resultsCount)}>
                      {totalCount} project{totalCount !== 1 ? 's' : ''} found
                    </div>
                  )}
                  {projects.map((project) => (
                    <button
                      key={project.id}
                      type="button"
                      className={css(styles.projectOption)}
                      onClick={() => handleSelect(project)}
                    >
                      <div className={css(styles.projectOptionHeader)}>
                        <span className={css(styles.projectLabel)}>
                          {project.label}
                        </span>
                        <span className={css(styles.projectDate)}>
                          {formatDate(
                            project.accepted_at || project.created_date,
                          )}
                        </span>
                      </div>
                      <div className={css(styles.projectOptionDetails)}>
                        {project.job_site && (
                          <span className={css(styles.projectDetail)}>
                            <i className="fa-solid fa-building" />
                            {project.job_site}
                          </span>
                        )}
                        {project.material && (
                          <span className={css(styles.projectDetail)}>
                            <i className="fa-solid fa-cubes" />
                            {project.material}
                          </span>
                        )}
                      </div>
                      {(project.pickup_address || project.dropoff_address) && (
                        <div className={css(styles.projectLocations)}>
                          {project.pickup_address && (
                            <span className={css(styles.projectLocation)}>
                              <i className="fa-solid fa-location-dot" />
                              {project.pickup_address}
                            </span>
                          )}
                          {project.dropoff_address && (
                            <span className={css(styles.projectLocation)}>
                              <i className="fa-solid fa-location-arrow" />
                              {project.dropoff_address}
                            </span>
                          )}
                        </div>
                      )}
                    </button>
                  ))}

                  {/* Sentinel for infinite scroll */}
                  <div ref={sentinelRef} className={css(styles.sentinel)}>
                    {isLoadingMore && (
                      <div className={css(styles.loadingMore)}>
                        <i className="fa-solid fa-spinner fa-spin" />
                        <span>Loading more...</span>
                      </div>
                    )}
                  </div>

                  {!hasMore && projects.length > 0 && (
                    <div className={css(styles.endMessage)}>
                      No more projects to load
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {hasError && <span className={css(styles.errorText)}>{error}</span>}

      <p className={css(styles.helpText)}>{helpText}</p>
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    position: 'relative',
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    marginBottom: '4px',
  },
  required: {
    color: '#EF4444',
  },
  searchContainer: {
    position: 'relative',
  },
  searchInputWrapper: {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    backgroundColor: '#FFFFFF',
    padding: '0 12px',
    ':focus-within': {
      borderColor: '#3B82F6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    },
  },
  searchInputWrapperError: {
    borderColor: '#EF4444',
    ':focus-within': {
      borderColor: '#EF4444',
      boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.1)',
    },
  },
  searchIcon: {
    color: '#9CA3AF',
    fontSize: '14px',
  },
  loadingIcon: {
    color: '#9CA3AF',
    fontSize: '14px',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    padding: '12px',
    fontSize: '14px',
    backgroundColor: 'transparent',
    '::placeholder': {
      color: '#9CA3AF',
    },
  },
  dropdown: {
    position: 'fixed',
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    border: '1px solid #E5E7EB',
    maxHeight: '300px',
    overflowY: 'auto',
    zIndex: 9999,
  },
  resultsCount: {
    padding: '8px 16px',
    fontSize: '12px',
    color: '#6B7280',
    borderBottom: '1px solid #F3F4F6',
    backgroundColor: '#F9FAFB',
  },
  emptyMessage: {
    padding: '16px',
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '14px',
  },
  projectOption: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '100%',
    padding: '12px 16px',
    border: 'none',
    borderBottom: '1px solid #F3F4F6',
    backgroundColor: '#FFFFFF',
    cursor: 'pointer',
    textAlign: 'left',
    ':hover': {
      backgroundColor: '#F9FAFB',
    },
    ':last-child': {
      borderBottom: 'none',
    },
  },
  projectOptionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  projectLabel: {
    fontWeight: 600,
    color: '#111827',
    fontSize: '14px',
  },
  projectDate: {
    color: '#6B7280',
    fontSize: '12px',
  },
  projectOptionDetails: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  projectDetail: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: '#6B7280',
    fontSize: '12px',
  },
  projectLocations: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    marginTop: '4px',
  },
  projectLocation: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: '#9CA3AF',
    fontSize: '11px',
  },
  selectedProject: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: '#EFF6FF',
    borderRadius: '8px',
    border: '1px solid #BFDBFE',
  },
  selectedProjectInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  selectedProjectLabel: {
    fontWeight: 600,
    color: '#1D4ED8',
    fontSize: '14px',
  },
  selectedProjectDetails: {
    display: 'flex',
    gap: '8px',
    color: '#3B82F6',
    fontSize: '12px',
  },
  separator: {
    color: '#93C5FD',
  },
  clearButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    border: 'none',
    borderRadius: '50%',
    backgroundColor: 'transparent',
    color: '#6B7280',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: '#E5E7EB',
      color: '#374151',
    },
  },
  errorText: {
    color: '#EF4444',
    fontSize: '12px',
    marginTop: '4px',
  },
  helpText: {
    color: '#6B7280',
    fontSize: '12px',
    marginTop: '4px',
  },
  // Skeleton loading styles
  skeletonItem: {
    padding: '12px 16px',
    borderBottom: '1px solid #F3F4F6',
  },
  skeletonHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  skeletonLabel: {
    width: '120px',
    height: '16px',
    backgroundColor: '#E5E7EB',
    borderRadius: '4px',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  skeletonDate: {
    width: '80px',
    height: '12px',
    backgroundColor: '#E5E7EB',
    borderRadius: '4px',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  skeletonDetails: {
    display: 'flex',
    gap: '12px',
  },
  skeletonDetail: {
    width: '100px',
    height: '12px',
    backgroundColor: '#E5E7EB',
    borderRadius: '4px',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  // Infinite scroll styles
  sentinel: {
    minHeight: '1px',
  },
  loadingMore: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    color: '#6B7280',
    fontSize: '14px',
  },
  endMessage: {
    padding: '12px',
    textAlign: 'center',
    color: '#9CA3AF',
    fontSize: '12px',
  },
})

export default ParentProjectSelector
